/**
 * Rules for recording a payment: the lines a member pays, whether they are valid, and what is sent.
 * The server enforces the same rules (see CompanyMemberContributionRequest); checking here just says so sooner.
 */
import moment from "moment";
import { formatNaira } from "../../utils/dashboardFormat";
import { kindOf } from "./contribModel";

/** The backend asks for a reference or code for these payment methods (cheque, transfer, card and the like). */
export const METHODS_NEEDING_CODE = [1, 3, 4];
export const needsCode = (methodId) => METHODS_NEEDING_CODE.includes(Number(methodId));

export const todayISO = (now = new Date()) => moment(now).format("YYYY-MM-DD");

export const emptyLine = (overrides = {}, now = new Date()) => ({ category: "", amount: "", method: "", date: todayISO(now), code: "", ...overrides });

const num = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : NaN;
};

/** Categories open for payments today (inside their start and end dates), ready for a picker. */
export function categoryOptions(rawCategories, now = new Date()) {
  const today = moment(now).startOf("day");
  return (Array.isArray(rawCategories) ? rawCategories : [])
    .filter((c) => c && c.id)
    .filter((c) => {
      if (c.start_date && moment(c.start_date).startOf("day").isAfter(today)) return false;
      if (c.end_date && moment(c.end_date).startOf("day").isBefore(today)) return false;
      return true;
    })
    .map((c) => ({
      id: String(c.id),
      name: c.name,
      kind: kindOf(c),
      max: Number(c.amount) > 0 ? Number(c.amount) : null,
      perMember: Number(c.threshold ?? c.amount_per_individuals) > 0 ? Number(c.threshold ?? c.amount_per_individuals) : null,
    }));
}

/** A member's standing in one category, from their per-category summary. */
export function standingFor(summary, categoryId) {
  const row = (summary?.categories || []).find((c) => String(c.id) === String(categoryId));
  if (!row) return null;
  return {
    paid: Number(row.paid) || 0,
    pending: Number(row.pending) || 0,
    perMember: row.amount_per_member != null ? Number(row.amount_per_member) : null,
    remaining: row.remaining != null ? Number(row.remaining) : null,
  };
}

/** Wording for what is still due, shown under the category picker. */
export function standingText(standing) {
  if (!standing) return "";
  if (standing.perMember) {
    if (standing.remaining === 0) return `Already paid up (${formatNaira(standing.paid)} of ${formatNaira(standing.perMember)})`;
    return `Paid ${formatNaira(standing.paid)} of ${formatNaira(standing.perMember)}, ${formatNaira(standing.remaining)} still due`;
  }
  return standing.paid ? `Paid ${formatNaira(standing.paid)} so far` : "";
}

/**
 * Validate every line. Returns one error object per line plus `ok`.
 * @param options.categories result of categoryOptions
 * @param options.summary the member's per-category summary (optional)
 */
export function validateLines(lines, { categories = [], summary = null, now = new Date() } = {}) {
  const today = moment(now).startOf("day");
  const seen = new Map();
  lines.forEach((l, i) => l.category && seen.set(l.category, [...(seen.get(l.category) || []), i]));

  const errors = lines.map((line) => {
    const e = {};
    const category = categories.find((c) => c.id === String(line.category));
    if (!line.category) e.category = "Choose a category";
    else if (seen.get(line.category).length > 1) e.category = "This category is already in another payment";

    const amount = num(line.amount);
    if (line.amount === "" || line.amount === null || line.amount === undefined) e.amount = "Enter an amount";
    else if (!Number.isFinite(amount) || amount <= 0) e.amount = "Amount must be more than 0";
    else {
      if (category?.max && amount > category.max) e.amount = `The most for this category is ${formatNaira(category.max)}`;
      const standing = line.category ? standingFor(summary, line.category) : null;
      if (!e.amount && standing?.perMember && amount > standing.remaining) {
        e.amount = standing.remaining === 0 ? "This member has already paid up here" : `More than the ${formatNaira(standing.remaining)} still due`;
      }
    }

    if (!line.method) e.method = "Choose how it was paid";
    if (!line.date) e.date = "Choose the payment date";
    else if (moment(line.date).startOf("day").isAfter(today)) e.date = "The date can't be in the future";
    if (line.method && needsCode(line.method) && !String(line.code || "").trim()) e.code = "Enter the reference or cheque number";
    return e;
  });

  return { errors, ok: errors.every((e) => Object.keys(e).length === 0) };
}

export const totalOf = (lines) => lines.reduce((sum, l) => sum + (Number.isFinite(num(l.amount)) ? num(l.amount) : 0), 0);

/** What the server expects. Amounts keep their kobo; a reference is sent as text for the methods that need one. */
export function buildPayload(memberId, lines) {
  return {
    member_id: memberId,
    fields: lines.map((l) => {
      const field = {
        contribution_category_id: l.category,
        payment_type_id: parseInt(l.method, 10),
        payment_date: l.date,
        amount: num(l.amount),
      };
      if (needsCode(l.method)) field.transaction_code = String(l.code || "").trim();
      return field;
    }),
  };
}

/** The most helpful amount to start a line with: what is still due, when we know it. */
export function suggestedAmount(summary, categoryId) {
  const s = standingFor(summary, categoryId);
  return s && s.perMember && s.remaining > 0 ? String(s.remaining) : "";
}
